import { useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Bell, Warning, Clock, CalendarBlank, FileText, Stamp, CurrencyDollar, FileArrowUp, Trash } from "@phosphor-icons/react";

const urgencyStyles = {
  overdue: { bg: "bg-[#FF2A00]/5 border-[#FF2A00]/30", badge: "bg-[#FF2A00]/10 text-[#FF2A00] border-[#FF2A00]/20", label: "OVERDUE", icon: Warning },
  urgent: { bg: "bg-[#FFD100]/5 border-[#FFD100]/30", badge: "bg-[#FFD100]/10 text-[#946B00] border-[#FFD100]/20", label: "URGENT", icon: Bell },
  upcoming: { bg: "bg-[#002FA7]/5 border-[#002FA7]/20", badge: "bg-[#002FA7]/10 text-[#002FA7] border-[#002FA7]/20", label: "UPCOMING", icon: Clock },
  future: { bg: "bg-white border-gray-200", badge: "bg-gray-100 text-[#6B7280] border-gray-200", label: "FUTURE", icon: CalendarBlank },
};
const typeIcons = { form486: FileText, form470: FileArrowUp, invoicing: CurrencyDollar, loa: Stamp, contract: FileText };
const typeLabels = { form486: "Form 486", form470: "Form 470", invoicing: "Invoicing", loa: "LOA", contract: "Contract" };

export default function Deadlines() {
  const [deadlines, setDeadlines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState("all");
  const [yearFilter, setYearFilter] = useState("all");
  const [dismissed, setDismissed] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem("dismissed_deadlines") || "[]")); }
    catch { return new Set(); }
  });

  useEffect(() => {
    api.get("/deadlines").then((r) => setDeadlines(r.data)).catch(() => toast.error("Failed to load deadlines")).finally(() => setLoading(false));
  }, []);

  const dismissDeadline = (id) => {
    const next = new Set(dismissed);
    next.add(id);
    setDismissed(next);
    localStorage.setItem("dismissed_deadlines", JSON.stringify([...next]));
    toast.success("Deadline dismissed");
  };

  // Extract unique funding years for dropdown
  const fundingYears = [...new Set(deadlines.map((d) => d.funding_year).filter(Boolean))].sort().reverse();

  const filtered = deadlines
    .filter((d) => typeFilter === "all" || d.type === typeFilter)
    .filter((d) => yearFilter === "all" || d.funding_year === yearFilter)
    .filter((d) => !dismissed.has(d.id));
  const overdue = filtered.filter((d) => d.urgency === "overdue");
  const urgent = filtered.filter((d) => d.urgency === "urgent");
  const upcoming = filtered.filter((d) => d.urgency === "upcoming");
  const future = filtered.filter((d) => d.urgency === "future");

  const DeadlineCard = ({ item }) => {
    const style = urgencyStyles[item.urgency] || urgencyStyles.future;
    const UrgIcon = style.icon;
    const TypeIcon = typeIcons[item.type] || FileText;
    return (
      <div className={`border p-4 ${style.bg} transition-colors hover:shadow-sm`} data-testid={`deadline-${item.id}`}>
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className="mt-0.5"><TypeIcon size={18} className="text-[#002FA7]" weight="bold" /></div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-[#1A1D20] leading-tight">{item.title}</p>
              <p className="text-xs text-[#6B7280] mt-1">{item.description}</p>
              {item.frn && <p className="text-xs font-mono text-[#002FA7] mt-1">FRN: {item.frn}</p>}
            </div>
          </div>
          <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
            <div className="flex items-center gap-2">
              <Badge className={`rounded-none text-[10px] border uppercase tracking-wider ${style.badge}`}>
                <UrgIcon size={10} weight="bold" className="mr-1" />{style.label}
              </Badge>
              <button data-testid={`dismiss-deadline-${item.id}`} onClick={() => dismissDeadline(item.id)} className="p-1 hover:bg-red-50 transition-colors" title="Dismiss deadline">
                <Trash size={14} className="text-[#FF2A00]" />
              </button>
            </div>
            <span className="text-xs font-semibold text-[#1A1D20]">
              {item.days_remaining < 0 ? `${Math.abs(item.days_remaining)}d overdue` : item.days_remaining === 0 ? "TODAY" : `${item.days_remaining}d left`}
            </span>
            <span className="text-[10px] text-[#6B7280]">{item.deadline_date}</span>
          </div>
        </div>
      </div>
    );
  };

  const Section = ({ title, items, count }) => {
    if (items.length === 0) return null;
    return (
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-3">
          <h2 className="text-xs uppercase tracking-[0.2em] font-semibold text-[#6B7280]">{title}</h2>
          <span className="text-xs font-bold text-[#1A1D20] bg-gray-100 px-2 py-0.5">{count}</span>
        </div>
        <div className="space-y-2">{items.map((item) => <DeadlineCard key={item.id} item={item} />)}</div>
      </div>
    );
  };

  if (loading) return <div className="p-8 flex items-center justify-center h-full"><p className="text-[#6B7280] text-sm">Loading deadlines...</p></div>;

  return (
    <div className="p-6" data-testid="deadlines-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#6B7280] mb-1">Alerts</p>
          <h1 className="text-2xl sm:text-3xl tracking-tight font-semibold text-[#1A1D20]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Deadline Reminders</h1>
          <p className="text-sm text-[#6B7280] mt-1">USAC filing windows, LOA expirations, contract renewals</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={yearFilter} onValueChange={setYearFilter}>
            <SelectTrigger data-testid="filter-funding-year" className="rounded-none w-[150px]"><SelectValue placeholder="All Years" /></SelectTrigger>
            <SelectContent className="rounded-none">
              <SelectItem value="all">All Years</SelectItem>
              {fundingYears.map((y) => <SelectItem key={y} value={y}>FY {y}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger data-testid="filter-deadline-type" className="rounded-none w-[150px]"><SelectValue placeholder="All Types" /></SelectTrigger>
            <SelectContent className="rounded-none">
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="form486">Form 486</SelectItem>
              <SelectItem value="form470">Form 470</SelectItem>
              <SelectItem value="invoicing">Invoicing</SelectItem>
              <SelectItem value="loa">LOA</SelectItem>
              <SelectItem value="contract">Contract</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Summary bar */}
      <div className="grid grid-cols-4 gap-0 border border-gray-200 mb-6">
        {[
          { label: "Overdue", count: overdue.length, color: "text-[#FF2A00]", bg: overdue.length > 0 ? "bg-[#FF2A00]/5" : "bg-white" },
          { label: "Urgent (0-30d)", count: urgent.length, color: "text-[#946B00]", bg: urgent.length > 0 ? "bg-[#FFD100]/5" : "bg-white" },
          { label: "Upcoming (30-90d)", count: upcoming.length, color: "text-[#002FA7]", bg: "bg-white" },
          { label: "Future", count: future.length, color: "text-[#6B7280]", bg: "bg-white" },
        ].map((s, i) => (
          <div key={i} className={`p-4 border-r border-gray-200 last:border-r-0 ${s.bg}`} data-testid={`deadline-summary-${i}`}>
            <span className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#6B7280]">{s.label}</span>
            <p className={`text-2xl font-bold tracking-tight mt-1 ${s.color}`} style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{s.count}</p>
          </div>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white border border-gray-200 p-12 text-center">
          <CalendarBlank size={32} className="mx-auto text-[#00B341] mb-3" />
          <p className="text-sm font-medium text-[#1A1D20]">No active deadlines</p>
          <p className="text-xs text-[#6B7280] mt-1">All filing windows are clear. Add forms with dates to start tracking.</p>
        </div>
      ) : (
        <>
          <Section title="Overdue" items={overdue} count={overdue.length} />
          <Section title="Urgent - Due within 30 days" items={urgent} count={urgent.length} />
          <Section title="Upcoming - Due within 90 days" items={upcoming} count={upcoming.length} />
          <Section title="Future" items={future} count={future.length} />
        </>
      )}
    </div>
  );
}
